import React, { useEffect, useState } from 'react';
import { Button, Loading } from 'react-style-guide';
import { TranslateFunction } from 'react-utilities';
import { createItemPurchase } from 'roblox-item-purchase';
import {
  Thumbnail2d,
  ThumbnailTypes,
  DefaultThumbnailSize,
  ThumbnailFormat
} from 'roblox-thumbnails';
import playButtonService from '../services/playButtonService';
import playButtonConstants from '../constants/playButtonConstants';
import { launchGame, launchLogin } from '../utils/playButtonUtils';
import {
  TGetProductInfo,
  TGetProductDetails,
  TPlayabilityStatus,
  TErrorComponent,
  TPlayComponent,
  TLoadingComponent,
  TPurchaseComponent
} from '../types/playButtonTypes';

const [ItemPurchase, itemPurchaseService] = createItemPurchase();

export const PlayButton = ({
  placeId,
  status,
  playComponent
}: {
  placeId: number;
  status: TPlayabilityStatus.Playable | TPlayabilityStatus.GuestProhibited;
  playComponent?: TPlayComponent;
}): JSX.Element => {
  return (
    <Button
      className='btn-full-width btn-common-play-game-lg'
      onClick={e => {
        e.preventDefault();
        if (status === TPlayabilityStatus.Playable) {
          launchGame(placeId);
        } else if (status === TPlayabilityStatus.GuestProhibited) {
          launchLogin(placeId);
        }
      }}>
      {!playComponent ? <span className='icon-common-play' /> : playComponent(status)}
    </Button>
  );
};

PlayButton.defaultProps = {
  playComponent: undefined
};

export const PurchaseButton = ({
  translate,
  universeId,
  placeId,
  onPurchaseSuccess,
  purchaseComponent,
  loadingComponent
}: {
  translate: TranslateFunction;
  universeId: number;
  placeId: number;
  onPurchaseSuccess: () => void;
  purchaseComponent?: TPurchaseComponent;
  loadingComponent?: TLoadingComponent;
}): JSX.Element => {
  const [productInfo, setProductInfo] = useState<TGetProductInfo | undefined>(undefined);
  const [productDetails, setProductDetails] = useState<TGetProductDetails | undefined>(undefined);

  useEffect(() => {
    const fetchProductInfo = async () => {
      try {
        const response = await playButtonService.getProductInfo([universeId]);
        setProductInfo(response);
      } catch (e) {
        console.log(e);
      }
    };

    const fetchProductDetails = async () => {
      try {
        const response = await playButtonService.getProductDetails([placeId]);
        setProductDetails(response);
      } catch (e) {
        console.log(e);
      }
    };

    // eslint-disable-next-line no-void
    void fetchProductInfo();
    // eslint-disable-next-line no-void
    void fetchProductDetails();
  }, []);

  if (productInfo === undefined || productDetails === undefined) {
    return !loadingComponent ? <Loading /> : loadingComponent();
  }

  return (
    <React.Fragment>
      <Button
        className='btn-full-width btn-common-play-game-lg'
        onClick={e => {
          e.preventDefault();
          itemPurchaseService.start();
        }}>
        {!purchaseComponent ? (
          <React.Fragment>
            <span className='icon-robux-white' />
            <span className='btn-text'>{productInfo.price}</span>{' '}
          </React.Fragment>
        ) : (
          purchaseComponent(productDetails, productInfo)
        )}
      </Button>
      <ItemPurchase
        {...{
          translate,
          productId: productInfo.productId,
          expectedPrice: productInfo.price,
          thumbnail: (
            <Thumbnail2d
              type={ThumbnailTypes.gameIcon}
              size={DefaultThumbnailSize}
              targetId={universeId}
              imgClassName='game-card-thumb'
              format={ThumbnailFormat.jpeg}
            />
          ),
          assetName: productDetails.name,
          assetType: 'Place',
          sellerName: productDetails.builder,
          expectedCurrency: 1,
          expectedSellerId: productInfo.sellerId,
          onPurchaseSuccess,
          isPlace: true
        }}
      />
    </React.Fragment>
  );
};

PurchaseButton.defaultProps = {
  purchaseComponent: undefined,
  loadingComponent: undefined
};

export const Error = ({
  translate,
  playabilityStatus,
  errorComponent
}: {
  playabilityStatus: Exclude<
    TPlayabilityStatus,
    | TPlayabilityStatus.Playable
    | TPlayabilityStatus.GuestProhibited
    | TPlayabilityStatus.PurchaseRequired
  >;
  translate: TranslateFunction;
  errorComponent?: TErrorComponent;
}): JSX.Element =>
  !errorComponent ? (
    <span className='error-message'>
      {translate(playButtonConstants.playButtonTranslationMap[playabilityStatus])}
    </span>
  ) : (
    errorComponent(playabilityStatus)
  );

Error.defaultProps = {
  errorComponent: undefined
};
